import React from 'react'
import { Box, Typography, Skeleton, useTheme, alpha } from '@mui/material'
import { ResponsiveLine } from '@nivo/line'
import { useTranslation } from 'react-i18next'

const ActiveUsersChart = ({ data, loading, height = 300, title }) => {
  const { t } = useTranslation()
  const chartTitle = title || t('analytics_active_users')
  const theme = useTheme()
  const goldColor = theme.palette.primary.main

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={height} sx={{ borderRadius: '8px' }} />
  }

  const chartData = [
    {
      id: 'active_users',
      data: (data || []).map(d => ({ x: d.date, y: d.count })),
    },
  ]

  const chartTheme = {
    axis: {
      ticks: {
        text: { fill: theme.palette.text.secondary, fontSize: 11 },
      },
    },
    grid: {
      line: {
        stroke: alpha(theme.palette.divider, 0.25),
        strokeWidth: 1,
      },
    },
  }

  return (
    <Box sx={{ height, minHeight: 200 }}>
      <ResponsiveLine
        data={chartData}
        theme={chartTheme}
        margin={{ top: 12, right: 20, bottom: 36, left: 48 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, stacked: false }}
        curve="monotoneX"
        enableArea={true}
        areaOpacity={0.06}
        areaBaselineValue={0}
        axisBottom={{
          tickSize: 0,
          tickPadding: 8,
          tickRotation: -45,
          tickValues: data && data.length > 14 ? 'every 3 days' : undefined,
          format: v => {
            const parts = v.split('-')
            return parts.length >= 3 ? `${parts[1]}/${parts[2]}` : v
          },
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          tickValues: 5,
        }}
        colors={[goldColor]}
        lineWidth={2.5}
        pointSize={data && data.length > 20 ? 0 : 6}
        pointColor={{ theme: 'background' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        useMesh={true}
        enableGridX={false}
        enableGridY={true}
        enableSlices="x"
        sliceTooltip={({ slice }) => (
          <Box
            sx={{
              background: theme.palette.background.paper,
              p: '8px 12px',
              borderRadius: '8px',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 24px rgba(0, 0, 0, 0.6)'
                : '0 4px 16px rgba(0, 0, 0, 0.08)',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
              {slice.points[0]?.data?.x}
            </Typography>
            {slice.points.map(point => (
              <Box key={point.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: point.serieColor }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {point.data.yFormatted} {t('analytics_users_label')}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      />
    </Box>
  )
}

export default ActiveUsersChart
